import React from 'react';

type ConnectorType = 'solid' | 'dashed';

type Variant = 'RedeemSteps';

const VARIANT_DEFAULTS: Record<Variant, {
  connectorType: ConnectorType;
  badgeClassName: string;
  badgeTextClassName: string;
  stepTextClassName: string;
  connectorClassName: string;
  stepGapClassName: string;
}> = {
  RedeemSteps: {
    connectorType: 'dashed',
    badgeClassName: 'w-5 h-5 rounded-full flex items-center justify-center shrink-0',
    badgeTextClassName: 'font-bold text-xs leading-none',
    stepTextClassName: 'm-0 flex-1 ml-2 text-xs leading-[1rem]',
    connectorClassName: 'w-px border-l',
    stepGapClassName: 'pb-4',
  },
};

interface NumberedStepsProps {
  /** Step text content; rendered in order. Index = number shown on the badge. */
  steps: string[];
  variant?: Variant;
  /** Line style of the vertical connector between badges. */
  connectorType?: ConnectorType;
  /** Classes for the wrapping flex column. */
  className?: string;
  /** Classes for the numbered badge circle — set size, bg, radius here. */
  badgeClassName?: string;
  /** Classes for the number inside the badge — set font/color here. */
  badgeTextClassName?: string;
  /** Classes for the step text (right column) — set font/color here. */
  stepTextClassName?: string;
  /**
   * Extra classes for the connector — set color/width here. The line style
   * (`solid`/`dashed`) is owned by this component via `connectorType` so the
   * dashes are guaranteed to render regardless of consumer Tailwind setup.
   */
  connectorClassName?: string;
  /** Class applied to non-last step text (controls inter-row spacing). */
  stepGapClassName?: string;
}

/**
 * Vertical numbered list with a connector line between badges.
 * Layout + connector line-style only — colors, sizes, and fonts come from
 * the consumer via *ClassName props, so this component carries no
 * dependency on the consumer's Tailwind config.
 */
export const NumberedSteps: React.FC<NumberedStepsProps> = props => {
  const v = props.variant !== undefined ? VARIANT_DEFAULTS[props.variant] : undefined;
  const {
    steps,
    className = '',
    connectorType = v?.connectorType ?? 'solid',
  } = props;
  const badgeClassName = `${v?.badgeClassName ?? ''} ${props.badgeClassName ?? ''}`.trim();
  const badgeTextClassName = `${v?.badgeTextClassName ?? ''} ${props.badgeTextClassName ?? ''}`.trim();
  const stepTextClassName = `${v?.stepTextClassName ?? ''} ${props.stepTextClassName ?? ''}`.trim();
  const connectorClassName = `${v?.connectorClassName ?? ''} ${props.connectorClassName ?? ''}`.trim();
  const stepGapClassName = `${v?.stepGapClassName ?? ''} ${props.stepGapClassName ?? ''}`.trim();
  return (
  <div className={`flex flex-col w-full ${className}`}>
    {steps.map((step, i) => {
      const isLast = i === steps.length - 1;
      return (
        <div key={i} className="flex items-stretch">
          {/* Left column: badge at top, connector fills remaining height */}
          <div className="flex flex-col items-center shrink-0">
            <div className={badgeClassName}>
              <span className={badgeTextClassName}>{i + 1}</span>
            </div>
            {!isLast && (
              <div
                className={connectorClassName}
                style={{ flex: 1, borderLeftStyle: connectorType }}
              />
            )}
          </div>
          {/* Right column: step text */}
          <p
            className={`${stepTextClassName} ${!isLast ? stepGapClassName : ''}`}
          >
            {step}
          </p>
        </div>
      );
    })}
  </div>
  );
};
